import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputBase,
} from "@mui/material";
import axios from "axios";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as yup from "yup";
import toast, { Toaster } from "react-hot-toast";
import useApi from "../../service/api.service";

const validationSchema = yup.object({
  Email: yup
    .string("Enter your email")
    .required("Email is required")
    .email("Email is not valid"),
  Password: yup
    .string("Enter your password")
    .matches(
      /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
      "Password must contain at least 8 characters, one uppercase, one number and one special case character"
    )
    .required("Password is required"),
  ResetPassword: yup
    .string("Enter your password")
    .matches(
      /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
      "Password must contain at least 8 characters, one uppercase, one number and one special case character"
    )
    .required("Password is required"),
  OTP: yup.string("Enter your OTP").required("OTP is required"),
});

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "260px",
  },
  title: {
    fontSize: "25px",
    fontWeight: 500,
    color: "#404040",
  },
  error: {
    color: "red",
  },
  formBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "28px",
  },
  forgotPassword: {
    textDecoration: "underline",
    fontSize: "13px",
    fontWeight: 500,
    color: "#9F9F9F",
    cursor: "pointer",
  },
  signInButton: {
    color: "white",
    backgroundColor: "#0077B6",
    borderRadius: "20px",
    height: "30px",
    width: "110px",
    border: "none",
    cursor: "pointer",
  },
  forgotPasswordButton: {
    color: "white",
    backgroundColor: "#0077B6",
    borderRadius: "20px",
    height: "30px",
    width: "140px",
    border: "none",
    cursor: "pointer",
  },
  forgotPasswordEmail: {
    color: "rgb(0, 163, 152)",
  },
};

function ForgetPassword({ setIsLoginView }) {
  const Header = ["Forgot Password", "OTP Verification", "Reset Password"];
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [step, setStep] = useState(0);
  const API = useApi();
  const formik = useFormik({
    initialValues: {
      Email: "",
      Password: "",
      ResetPassword: "",
      OTP: "",
    },

    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  const [resendTimer, setResendTimer] = useState(119);
  const [resendClickable, setResendClickable] = useState(false);
  useEffect(() => {
    let timer;
    if (!resendClickable) {
      timer = setInterval(() => {
        setResendTimer((prevTimer) => {
          if (prevTimer > 0) {
            return prevTimer - 1;
          } else {
            clearInterval(timer);
            setResendClickable(true);
            return 0;
          }
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [resendClickable]);
  const minutes = Math.floor(resendTimer / 60);
  const seconds = resendTimer % 60;
  const timerText = `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
  const handleResendClick = () => {
    setResendTimer(119);
    setResendClickable(false);
  };

  const intiateOTP = () => {
    toast.promise(
      (async () => {
        try {
          const customBody = {
            email: formik.values.Email,
          };
          const response = await API.verifyEmail(customBody);
          setStep(1);
          setResendTimer(119);
          return response;
        } catch (error) {
          console.error(
            "Error eith OTP initiation for forgot Password",
            error.response?.data || "Something went wrong"
          );
          throw error.response
            ? error.response
            : new Error("Network or server error");
        }
      })(),
      {
        loading: "Initiated password change. Check you mail for OTP",
        success: (response) =>
          response.data.message || "Password Change Initialted",
        error: (response) => {
          return response.data?.error?.message || "Failed to Change Password.";
        },
      }
    );
  };
  const authrorizeOTP = () => {
    setStep(2);
  };

  const handleFormSubmit = async (values) => {
    toast.promise(
      (async () => {
        try {
          // check password and confirm password.
          if (values.Password !== values.ResetPassword) {
            throw new Error("Password and reset Password doesn't match");
          }
          // check otp
          const customBody = {
            email: values.Email,
            inputedOtp: parseInt(values.OTP),
            newPassword: values.Password,
          };
          const response = await API.verifyOtp(customBody);
          setIsLoginView(true);
          return response;
        } catch (error) {
          console.error(
            "Forgot Password error, something went wrong",
            error.response?.data || "Something went wrong"
          );
          throw error.response
            ? error.response
            : new Error("Network or server error");
        }
      })(),
      {
        loading: "Password Change",
        success: (response) =>
          response.data.message || "Password Change Successful",
        error: (response) => {
          return response.data?.error?.message || "Failed to Change Password.";
        },
      }
    );
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowResetPassword = () => {
    setShowResetPassword(!showResetPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    formik.setFieldValue("Password", e.target.value);
  };

  const handleResetPasswordChange = (e) => {
    setResetPassword(e.target.value);
    formik.setFieldValue("ResetPassword", e.target.value);
  };

  const renderSteps = (step) => {
    if (step === 0) {
      return (
        <>
          <div className="form-group">
            <div className="label">Registered Email Address</div>
            <FormControl fullWidth>
              <InputBase
                fullWidth
                type="text"
                className="input-border"
                placeholder="enter email address"
                name="Email"
                value={formik.values.Email}
                onChange={formik.handleChange}
                variant="standard"
              />
              {formik.touched.Email && Boolean(formik.errors.Email) ? (
                <p style={{ color: "red" }}>
                  {formik.touched.Email && formik.errors.Email}
                </p>
              ) : null}
            </FormControl>
          </div>
          <div className="form-group">
            <div style={{ color: "#9F9F9F", fontSize: "11px" }}>
              Please share your registered email address. We’ll share an OTP to
              reset your password.
            </div>
          </div>
          <div className="form-btn" style={styles.formBtn}>
            <div
              style={styles.forgotPassword}
              onClick={(e) => setIsLoginView(true)}
              onMouseEnter={(e) => (e.target.style.color = "#FFA500")}
              onMouseLeave={(e) => (e.target.style.color = "#9F9F9F")}
            >
              Back to SignIn
            </div>
            <button
              style={styles.signInButton}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                intiateOTP();
              }}
            >
              Request OTP
            </button>
          </div>
        </>
      );
    }
    if (step === 1) {
      return (
        <>
          <div className="form-group">
            {/* <div className="label">
              Enter the OTP received on
              <span
                style={styles.forgotPasswordEmail}
              >{` ${formik.values.Email}`}</span>
            </div> */}
          </div>
          <div className="form-group">
            <div className="label">Enter OTP</div>
            <FormControl fullWidth>
              <InputBase
                fullWidth
                type="text"
                className="input-border"
                placeholder="enter OTP"
                name="OTP"
                value={formik.values.OTP}
                onChange={formik.handleChange}
                variant="standard"
              />
            </FormControl>
          </div>
          <div className="form-group">
            <div style={{ color: "#9F9F9F", fontSize: "11px" }}>
              Please enter the OTP received on your email to reset your
              password.
            </div>
          </div>
          <div className="form-group">
            <div style={{ color: "#9F9F9F", fontSize: "13px" }}>
              Didn’t received OTP?
              {resendClickable ? (
                <span
                  style={{ color: "#FFA500", cursor: "pointer" }}
                  onClick={handleResendClick}
                >
                  Resend
                </span>
              ) : (
                `Resend ${timerText}`
              )}
            </div>
          </div>
          <div className="form-group">
            <div
              style={{
                ...styles.forgotPassword,
                color: "#9F9F9F",
                fontSize: "13px",
              }}
              onClick={(e) => setStep(0)}
              onMouseEnter={(e) => (e.target.style.color = "#FFA500")}
              onMouseLeave={(e) => (e.target.style.color = "#9F9F9F")}
            >
              Incorrect Email Address?
            </div>
          </div>
          {/* <div className="form-group">
            <div className="label">Password</div>
            <FormControl fullWidth>
              <InputBase
                fullWidth
                type={showPassword ? "text" : "password"}
                placeholder="enter password"
                name="Password"
                className="input-border"
                value={password}
                onChange={handlePasswordChange}
                variant="standard"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                key={showPassword ? "visible" : "hidden"}
              />
              {formik.touched.Password && Boolean(formik.errors.Password) ? (
                <p style={styles.error}>
                  {formik.touched.Password && formik.errors.Password}
                </p>
              ) : null}
            </FormControl>
          </div>
          <div className="form-group">
            <div className="label">Confirm Password</div>
            <FormControl fullWidth>
              <InputBase
                fullWidth
                type={showResetPassword ? "text" : "password"}
                placeholder="Re-enter password"
                name="ResetPassword"
                className="input-border"
                value={resetPassword}
                onChange={handleResetPasswordChange}
                variant="standard"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowResetPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showResetPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                key={showPassword ? "visible" : "hidden"}
              />
              {formik.touched.Password && Boolean(formik.errors.Password) ? (
                <p style={styles.error}>
                  {formik.touched.Password && formik.errors.Password}
                </p>
              ) : null}
            </FormControl>
          </div> */}
          <div
            className="form-btn"
            style={{ ...styles.formBtn, justifyContent: "flex-end" }}
          >
            <button
              style={styles.forgotPasswordButton}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                authrorizeOTP();
              }}
            >
              Submit
            </button>
          </div>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <div className="form-group">
            <div className="label">Enter New Password</div>
            <FormControl fullWidth>
              <InputBase
                fullWidth
                type={showPassword ? "text" : "password"}
                placeholder="enter password"
                name="Password"
                className="input-border"
                value={password}
                onChange={handlePasswordChange}
                variant="standard"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                key={showPassword ? "visible" : "hidden"}
              />
              {formik.touched.Password && Boolean(formik.errors.Password) ? (
                <p style={styles.error}>
                  {formik.touched.Password && formik.errors.Password}
                </p>
              ) : null}
            </FormControl>
          </div>
          <div className="form-group">
            <div className="label">Confirm New Password</div>
            <FormControl fullWidth>
              <InputBase
                fullWidth
                type={showResetPassword ? "text" : "password"}
                placeholder="Re-enter password"
                name="ResetPassword"
                className="input-border"
                value={resetPassword}
                onChange={handleResetPasswordChange}
                variant="standard"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowResetPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showResetPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                key={showPassword ? "visible" : "hidden"}
              />
              {formik.touched.Password && Boolean(formik.errors.Password) ? (
                <p style={styles.error}>
                  {formik.touched.Password && formik.errors.Password}
                </p>
              ) : null}
            </FormControl>
          </div>
          <div
            className="form-btn"
            style={{ ...styles.formBtn, justifyContent: "flex-end" }}
          >
            <button style={styles.forgotPasswordButton} type="submit">
              Reset Password
            </button>
          </div>
        </>
      );
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.title}>{Header[step]}</div>
      <form onSubmit={formik.handleSubmit}>{renderSteps(step)}</form>
      <Toaster />
    </div>
  );
}

export default ForgetPassword;
